"use strict";
import { getAccount } from "@wagmi/core";
import { popupRegistry } from "components/Popups/registry";
import { PopupType } from "components/Popups/types";
import { wagmiConfig } from "components/Web3Provider/wagmiConfig";
import { getSigner, watchForInterruption } from "state/sagas/transactions/utils";
import { addTransaction } from "state/transactions/reducer";
import { call, put } from "typed-redux-saga";
const CURRENT_SEND_CALLS_VERSION = "2.0.0";
const EIP_5792_STATUS_FAILURE_THRESHOLD = 400;
async function sendCalls(params) {
  const { signer, batchedTxRequests, from } = params;
  const chainId = `0x${params.chainId.toString(16)}`;
  const calls = batchedTxRequests.map(({ to, data, value }) => ({ to, data, value }));
  const result = await signer.provider.send("wallet_sendCalls", [
    { version: CURRENT_SEND_CALLS_VERSION, calls, from, chainId, atomicRequired: true }
  ]);
  const batchId = result?.id;
  if (!batchId) {
    throw new Error("wallet_sendCalls returned no batch id");
  }
  const isSmartContractAccount = await hasOnChainCode(signer, from);
  let status;
  try {
    status = await signer.provider.send("wallet_getCallsStatus", [batchId]);
  } catch (err) {
    if (isSmartContractAccount) {
      return batchId;
    }
    throw new Error(
      `wallet_getCallsStatus failed; cannot verify atomic batch execution: ${err?.message ?? err}`
    );
  }
  if (!isSmartContractAccount && status?.atomic !== true) {
    throw new Error("wallet did not execute calls atomically despite atomicRequired");
  }
  if (typeof status?.status === "number" && status.status >= EIP_5792_STATUS_FAILURE_THRESHOLD) {
    throw new Error(`wallet_getCallsStatus returned failure status ${status.status}`);
  }
  return batchId;
}
async function hasOnChainCode(signer, address) {
  try {
    const code = await signer.provider.getCode(address);
    if (!code || code === "0x") {
      return false;
    }
    const normalized = code.toLowerCase();
    if (normalized.startsWith("0xef0100") && normalized.length === 48) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}
export function* handleAtomicSendCalls(params) {
  const { step, info, account, ignoreInterrupt } = params;
  const from = account.address;
  const { batchedTxRequests } = step;
  const chainId = batchedTxRequests[0].chainId;
  const { throwIfInterrupted } = yield* watchForInterruption(ignoreInterrupt);
  const signer = yield* call(getSigner, account.address);
  const batchId = yield* call(() => sendCalls({ signer, batchedTxRequests, from, chainId }));
  const connectorId = getAccount(wagmiConfig).connector?.id;
  const batchInfo = { connectorId, batchId, chainId };
  yield* put(addTransaction({ from: account.address, info, hash: batchId, chainId, batchInfo }));
  popupRegistry.addPopup({ type: PopupType.Transaction, hash: batchId }, batchId);
  yield* call(throwIfInterrupted);
  return batchId;
}
